import React from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Mountain, ArrowLeft, Printer, MessageCircle } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const ACCENT = '#3a5a40';
const SITE = 'https://gohimalayan.com';
const WA_NUMBER = '919871155963';

const PACK_QRS = [
  { type: 'url', label: 'Visit GoHimalayan', sub: 'gohimalayan.com', url: `${SITE}/`, fg: '#0D1B2A' },
  { type: 'url', label: 'Curated Stays', sub: 'Browse handpicked homestays', url: `${SITE}/discovered`, fg: '#0D1B2A' },
  { type: 'url', label: 'AI Trip Planner', sub: 'Plan your Himalayan trip', url: `${SITE}/plan`, fg: '#2C5F8D' },
  { type: 'url', label: 'The Himalayan Store', sub: 'Artisan products from the mountains', url: `${SITE}/store`, fg: '#C4973B' },
  { type: 'url', label: 'Itineraries', sub: 'Curated multi-day journeys', url: `${SITE}/itineraries`, fg: '#1B7A6E' },
  { type: 'url', label: 'Partner With Us', sub: 'For homestay hosts', url: `${SITE}/partner`, fg: '#3a5a40' },
  { type: 'whatsapp', label: 'WhatsApp us', sub: 'Start a conversation directly', url: `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Hi GoHimalayan!')}`, fg: '#075E54' },
  { type: 'url', label: 'All Links', sub: 'Everything GoHimalayan', url: `${SITE}/links`, fg: '#0D1B2A' },
];

export default function QRStarterPack() {
  return (
    <div className="min-h-screen bg-stone-100 print:bg-white" data-testid="qr-starter-pack">
      {/* Header — hidden in print */}
      <header className="bg-white border-b border-stone-200 print:hidden">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Mountain className="h-7 w-7" style={{ color: ACCENT }} strokeWidth={2.5} />
            <span className="text-lg font-bold tracking-tight text-stone-900" style={pf}>GoHimalayan</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/qr" className="flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors text-sm">
              <ArrowLeft className="h-4 w-4" />Back to Generator
            </Link>
            <button onClick={() => window.print()} className="flex items-center gap-2 px-5 py-2 bg-stone-900 text-white rounded-full text-sm font-semibold hover:bg-stone-700 transition-colors" data-testid="print-pack-btn">
              <Printer className="w-4 h-4" />Print / Save as PDF
            </button>
          </div>
        </div>
      </header>

      {/* Instructions — hidden in print */}
      <section className="py-10 px-6 print:hidden">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase font-semibold mb-3" style={{ color: ACCENT }}>Printable Starter Pack</p>
          <h1 className="text-3xl font-bold text-stone-900 mb-3" style={pf}>An A4 sheet of QR codes — ready to print, cut, and use.</h1>
          <p className="text-stone-500 text-sm max-w-xl mx-auto">
            Click <span className="font-semibold text-stone-700">Print / Save as PDF</span> above. In the print dialog, choose "Save as PDF" if you want a digital copy. Cut along the dashed lines to get 8 pocket-sized cards — perfect for property check-ins, retail counters, and pitch handovers.
          </p>
        </div>
      </section>

      {/* THE A4 SHEET */}
      <div className="flex justify-center pb-16 print:pb-0">
        <div
          id="qr-sheet"
          className="bg-white shadow-xl print:shadow-none print:rounded-none"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '15mm',
          }}
          data-testid="qr-sheet"
        >
          {/* Sheet header */}
          <div className="text-center mb-6 pb-4 border-b border-stone-200">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Mountain className="w-5 h-5" style={{ color: ACCENT }} strokeWidth={2.5} />
              <span className="text-base font-bold tracking-tight text-stone-900" style={pf}>GoHimalayan</span>
            </div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-stone-400 font-semibold">QR Starter Pack · Cut along the lines</p>
          </div>

          {/* 4×2 grid of QR cards */}
          <div className="grid grid-cols-2 gap-0">
            {PACK_QRS.map((q, i) => (
              <div
                key={i}
                className="border border-dashed border-stone-300 p-4 flex flex-col items-center justify-center text-center"
                style={{ aspectRatio: '1 / 1.05' }}
                data-testid={`pack-card-${i}`}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  {q.type === 'whatsapp' ? (
                    <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" strokeWidth={2.5} />
                  ) : (
                    <Mountain className="w-3.5 h-3.5" style={{ color: ACCENT }} strokeWidth={2.5} />
                  )}
                  <span className="text-[10px] font-bold tracking-tight text-stone-900" style={pf}>GoHimalayan</span>
                </div>

                <QRCodeSVG
                  value={q.url}
                  size={140}
                  bgColor="#FFFFFF"
                  fgColor={q.fg}
                  level="H"
                  includeMargin={false}
                  imageSettings={{
                    src: '/favicon.svg',
                    height: 26,
                    width: 26,
                    excavate: true,
                  }}
                />

                <p className="text-xs font-bold text-stone-900 mt-3" style={pf}>{q.label}</p>
                <p className="text-[9px] text-stone-500 mt-0.5 leading-tight px-2">{q.sub}</p>
                <p className="text-[8px] text-stone-300 mt-1.5 tracking-wider truncate max-w-full">
                  {q.type === 'whatsapp' ? `+${WA_NUMBER}` : q.url.replace('https://', '')}
                </p>
              </div>
            ))}
          </div>

          {/* Sheet footer */}
          <div className="text-center mt-6 pt-4 border-t border-stone-200">
            <p className="text-[8px] text-stone-400 tracking-wider">© 2026 GoHimalayan · gohimalayan.com · Printed in the spirit of the mountains</p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          body { background: white !important; }
          #qr-sheet {
            box-shadow: none !important;
            page-break-after: avoid;
          }
        }
      `}</style>
    </div>
  );
}
